import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
  meta?: {
    page?: number;
    limit?: number;
    total?: number;
    totalPages?: number;
  };
}

export type DiscountType = 'percentage' | 'fixed_amount';
export type DiscountDuration = 'once' | 'repeating' | 'forever';

export interface DiscountCode {
  id: string;
  code: string;
  couponId: string;
  active: boolean;
  discountType: DiscountType;
  discountValue: number;
  currency?: string;
  duration: DiscountDuration;
  durationInMonths?: number;
  maxRedemptions?: number;
  timesRedeemed: number; // From Stripe
  trackedRedemptions?: number; // From our database
  trackedRedemptionsList?: Array<{
    id: number;
    userId?: number;
    customerId?: string;
    userEmail?: string;
    redeemedAt: string;
    invoiceId?: string;
    paymentIntentId?: string;
  }>; // List of tracked redemptions
  expiresAt?: string;
  firstTimeTransactionOnly?: boolean;
  minimumAmount?: number;
  createdAt: string;
  updatedAt?: string;
}

export interface CreateDiscountCodeRequest {
  code?: string;
  discountType: DiscountType;
  discountValue: number;
  currency?: string;
  duration: DiscountDuration;
  durationInMonths?: number;
  maxRedemptions?: number;
  expiresAt?: string;
  firstTimeTransactionOnly?: boolean;
  minimumAmount?: number;
  active?: boolean;
  useTestMode?: boolean; // If true, creates discount code in Stripe test mode
}

export interface UpdateDiscountCodeRequest {
  active?: boolean;
  maxRedemptions?: number;
  expiresAt?: string;
}

export interface DiscountCodeFilters {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
  active?: boolean;
  code?: string;
  discountType?: DiscountType;
  expired?: boolean;
}

interface PaginatedResponse<T> extends ApiResponse<T[]> {
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

const transformErrorResponse = (response: any) => {
  if (response?.data?.message) {
    return { error: response.data.message };
  }
  if (response?.data?.error) {
    return { error: response.data.error };
  }
  return { error: 'An error occurred' };
};

export const discountCodesApi = createApi({
  reducerPath: 'discountCodesApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api/v1/admin/discount-codes',
    prepareHeaders: (headers, { getState }) => {
      const state = getState() as any;
      const token = state.auth?.token || localStorage.getItem('admin_token');
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['DiscountCode'],
  endpoints: (builder) => ({
    // List discount codes with filters
    getDiscountCodes: builder.query<PaginatedResponse<DiscountCode>, DiscountCodeFilters>({
      query: (filters = {}) => {
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            params.append(key, String(value));
          }
        });
        const queryString = params.toString();
        return `/?${queryString}`;
      },
      transformResponse: (response: ApiResponse<DiscountCode[]>) => {
        if (response.success) {
          return {
            success: true,
            data: response.data ?? [],
            meta: {
              page: response.meta?.page ?? 1,
              limit: response.meta?.limit ?? (response.data?.length ?? 0),
              total: response.meta?.total ?? (response.data?.length ?? 0),
              totalPages: response.meta?.totalPages ?? 1,
            },
          } as PaginatedResponse<DiscountCode>;
        }
        throw new Error(response.message || 'Failed to fetch discount codes');
      },
      transformErrorResponse,
      providesTags: ['DiscountCode'],
    }),

    // Get discount code by ID
    getDiscountCodeById: builder.query<DiscountCode, string>({
      query: (id) => `/${id}`,
      transformResponse: (response: ApiResponse<DiscountCode>) => {
        if (response.success && response.data) {
          return response.data;
        }
        throw new Error(response.message || 'Failed to fetch discount code');
      },
      transformErrorResponse,
      providesTags: (_result, _error, id) => [{ type: 'DiscountCode', id }],
    }),

    // Create discount code
    createDiscountCode: builder.mutation<DiscountCode, CreateDiscountCodeRequest>({
      query: (body) => ({
        url: '/',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiResponse<DiscountCode>) => {
        if (response.success && response.data) {
          return response.data;
        }
        throw new Error(response.message || 'Failed to create discount code');
      },
      transformErrorResponse,
      invalidatesTags: ['DiscountCode'],
    }),

    // Update discount code
    updateDiscountCode: builder.mutation<DiscountCode, { id: string; data: UpdateDiscountCodeRequest }>({
      query: ({ id, data }) => ({
        url: `/${id}`,
        method: 'PATCH',
        body: data,
      }),
      transformResponse: (response: ApiResponse<DiscountCode>) => {
        if (response.success && response.data) {
          return response.data;
        }
        throw new Error(response.message || 'Failed to update discount code');
      },
      transformErrorResponse,
      invalidatesTags: (_result, _error, { id }) => [{ type: 'DiscountCode', id }, 'DiscountCode'],
    }),

    // Delete (deactivate) discount code
    deleteDiscountCode: builder.mutation<{ success: boolean; message: string }, string>({
      query: (id) => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      transformResponse: (response: ApiResponse<any>) => {
        if (response.success) {
          return { success: true, message: response.message || 'Discount code deleted successfully' };
        }
        throw new Error(response.message || 'Failed to delete discount code');
      },
      transformErrorResponse,
      invalidatesTags: ['DiscountCode'],
    }),

    // Validate discount code (without redeeming)
    validateDiscountCode: builder.mutation<{
      valid: boolean;
      discountAmount: number;
      finalAmount: number;
      message?: string;
      codeDetails?: DiscountCode;
    }, string>({
      query: (code) => ({
        url: `/validate/${encodeURIComponent(code)}`,
        method: 'GET',
      }),
      transformResponse: (response: ApiResponse<{
        valid: boolean;
        discountAmount: number;
        finalAmount: number;
        message?: string;
        codeDetails?: DiscountCode;
      }>) => {
        if (response.success && response.data) {
          return response.data;
        }
        throw new Error(response.message || 'Failed to validate discount code');
      },
      transformErrorResponse,
    }),

    // Get comprehensive metrics for all discount codes
    getDiscountCodeMetrics: builder.query<ApiResponse<DiscountCodeMetrics>, void>({
      query: () => '/metrics',
      transformResponse: (response: ApiResponse<DiscountCodeMetrics>) => response,
      transformErrorResponse,
      providesTags: ['DiscountCode'],
    }),

    // Get detailed usage statistics for a specific discount code
    getDiscountCodeUsageStats: builder.query<ApiResponse<DiscountCodeUsageStats>, string>({
      query: (id) => `/${id}/stats`,
      transformResponse: (response: ApiResponse<DiscountCodeUsageStats>) => response,
      transformErrorResponse,
      providesTags: (_result, _error, id) => [{ type: 'DiscountCode', id }],
    }),

    // Debug: Force expiration of a discount code
    forceExpireDiscountCode: builder.mutation<DiscountCode, string>({
      query: (id) => ({
        url: `/${id}/force-expire`,
        method: 'POST',
      }),
      transformResponse: (response: ApiResponse<DiscountCode>) => {
        if (response.success && response.data) {
          return response.data;
        }
        throw new Error(response.message || 'Failed to force expiration');
      },
      transformErrorResponse,
      invalidatesTags: (_result, _error, id) => [{ type: 'DiscountCode', id }, 'DiscountCode'],
    }),

    // Reactivate discount code with new expiration date
    reactivateDiscountCode: builder.mutation<DiscountCode, { id: string; newExpiresAt?: string; extendByDays?: number }>({
      query: ({ id, ...body }) => ({
        url: `/${id}/reactivate`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiResponse<DiscountCode>) => {
        if (response.success && response.data) {
          return response.data;
        }
        throw new Error(response.message || 'Failed to reactivate discount code');
      },
      transformErrorResponse,
      invalidatesTags: (_result, _error, { id }) => [{ type: 'DiscountCode', id }, 'DiscountCode'],
    }),
  }),
});

// Metrics and Stats Types
export interface DiscountCodeMetrics {
  totalCodes: number;
  activeCodes: number;
  inactiveCodes: number;
  expiredCodes: number;
  usedCodes: number;
  unusedCodes: number;
  totalRedemptions: number;
  totalDiscountAmount: number;
  averageRedemptionsPerCode: number;
  mostUsedCode?: {
    code: string;
    redemptions: number;
    discountGiven: number;
  };
  codesByStatus: {
    active: number;
    inactive: number;
    expired: number;
    maxUsesReached: number;
  };
  codesByType: {
    percentage: number;
    fixed_amount: number;
  };
  recentActivity: {
    codesCreatedLast7Days: number;
    codesCreatedLast30Days: number;
    redemptionsLast7Days: number;
    redemptionsLast30Days: number;
  };
  performance?: {
    topPerformers: Array<{
      code: string;
      codeId: string;
      redemptions: number;
      discountGiven: number;
      efficiency: number;
    }>;
    underperformers: Array<{
      code: string;
      codeId: string;
      redemptions: number;
      daysSinceCreation: number;
    }>;
    unusedCodes: Array<{
      code: string;
      codeId: string;
      daysSinceCreation: number;
      expiresAt?: string;
    }>;
  };
  alerts?: {
    expiringToday?: Array<{
      code: string;
      codeId: string;
      expiresAt: string;
      discountValue: number;
      discountType: 'percentage' | 'fixed_amount';
      timesRedeemed: number;
      maxRedemptions?: number;
    }>;
    expiringSoon: Array<{
      code: string;
      codeId: string;
      expiresAt: string;
      daysUntilExpiry: number;
    }>;
    nearMaxUses: Array<{
      code: string;
      codeId: string;
      redemptions: number;
      maxRedemptions: number;
      usagePercentage: number;
    }>;
    inactiveHighValue: Array<{
      code: string;
      codeId: string;
      discountValue: number;
      discountType: 'percentage' | 'fixed_amount';
      daysSinceCreation: number;
    }>;
    expiredCodes: Array<{
      code: string;
      codeId: string;
      expiresAt: string;
      discountValue: number;
      discountType: 'percentage' | 'fixed_amount';
      timesRedeemed: number;
      maxRedemptions?: number;
    }>;
  };
  efficiency?: {
    averageDiscountPerCode: number;
    averageDiscountPerRedemption: number;
    conversionRate: number;
    averageDaysToFirstUse?: number;
    codesByDuration: {
      once: { count: number; avgRedemptions: number; avgDiscount: number };
      repeating: { count: number; avgRedemptions: number; avgDiscount: number };
      forever: { count: number; avgRedemptions: number; avgDiscount: number };
    };
  };
  financial?: {
    totalDiscountGiven: number;
    estimatedRevenueImpact: number;
    averageDiscountPercentage: number;
    discountDistribution: {
      under10: number;
      between10and25: number;
      between25and50: number;
      over50: number;
    };
  };
}

export interface DiscountCodeUsageStats {
  code: string;
  codeId: string;
  totalRedemptions: number;
  maxRedemptions?: number;
  remainingRedemptions?: number;
  isFullyUsed: boolean;
  isSingleUse: boolean;
  usagePercentage: number;
  totalDiscountGiven: number;
  averageDiscountPerRedemption: number;
  firstUsedAt?: string;
  lastUsedAt?: string;
  status: 'active' | 'inactive' | 'expired' | 'max_uses_reached' | 'unused';
  redemptionHistory?: Array<{
    date: string;
    count: number;
  }>;
}

export const {
  useGetDiscountCodesQuery,
  useGetDiscountCodeByIdQuery,
  useCreateDiscountCodeMutation,
  useUpdateDiscountCodeMutation,
  useDeleteDiscountCodeMutation,
  useValidateDiscountCodeMutation,
  useGetDiscountCodeMetricsQuery,
  useGetDiscountCodeUsageStatsQuery,
  useForceExpireDiscountCodeMutation,
  useReactivateDiscountCodeMutation,
} = discountCodesApi;

